import api from './api';

// User Authentication Services
export const userService = {
  // Auth endpoints
  register: (userData) => api.post('/api/v1/user/register-user', userData),
  login: (credentials) => api.post('/api/v1/user/login-user', credentials),
  loginAndActivate: (credentials) => api.post('/api/v1/user/login-and-activate', credentials),
  logout: () => api.post('/api/v1/user/logout-user'),
  getUserProfile: () => api.get('/api/v1/user/me/profile'),
  deactivateAccount: (deactivateData) => api.post('/api/v1/user/deactivate', deactivateData),
  activateAccount: () => api.post('/api/v1/user/activate'),
  deleteAccount: () => api.post('/api/v1/user/delete-user'),

  // Forgot Password endpoints
  verifyUserForReset: (userData) => api.post('/api/v1/user/verify-user-for-reset', userData),
  verifyOTP: (otpData) => api.post('/api/v1/user/verify-otp', otpData),
  resendOTP: (userData) => api.post('/api/v1/user/resend-otp', userData),
  resetPassword: (resetData) => api.post('/api/v1/user/reset-password', resetData),

  // Account Recovery
  requestAccountRecovery: (recoveryData) => api.post('/api/v1/user/request-account-recovery', recoveryData),
  getAccountRecoveryRequests: () => api.get('/api/v1/user/admin/account-recovery-requests'),
  undeleteAccount: (requestData) => api.post('/api/v1/user/admin/undelete-account', requestData),

  // Admin endpoints
  getAllUsers: () => api.get('/api/v1/user/admin/getAllUsers'),
  updateUserAsAdmin: (userId, userData) => api.post(`/api/v1/user/admin/updateUserAsAdmin/${userId}`, userData),
  getSingleUser: (userId) => api.get(`/api/v1/user/admin/GetSingleUser/${userId}`),
  deleteUserAsAdmin: (userId) => api.post(`/api/v1/user/admin/DeleteUser/${userId}`),
  // Note: These endpoints don't exist in backend - using sample data instead
  blockUser: (userId) => Promise.resolve({ data: { success: true, message: 'User blocked successfully' } }),
  unblockUser: (userId) => Promise.resolve({ data: { success: true, message: 'User unblocked successfully' } }),
  deleteUser: (userId) => Promise.resolve({ data: { success: true, message: 'User deleted successfully' } }),
};

// Player Services
export const playerService = {
  createPlayers: () => api.get('/api/v1/player/create'),
  getPlayerDetails: (playerId) => api.get(`/api/v1/player/playerDetails/${playerId}`),
  getPlayerImageDetails: (imageId) => api.get(`/api/v1/player/playerImageDetails/${imageId}`),
  getAllPlayers: (params) => api.get('/api/v1/player/all', { params }),
  getPlayerStats: () => Promise.resolve({ data: { success: true, data: [] } }),
  getPlayersByTeam: (teamName) => Promise.resolve({ data: { success: true, data: [] } }),
};

// Team Services
export const teamService = {
  createTeamsV2: (teamType) => api.get(`/api/v1/team/create-teams-v2/${teamType}`),
  getTeams: (teamIds = null) => {
    const payload = teamIds && teamIds.length > 0 ? { teamIds } : {};
    return api.post('/api/v1/team/get-teams', payload);
  },
  getAllTeams: (teamIds = null) => {
    const payload = teamIds && teamIds.length > 0 ? { teamIds } : {};
    return api.post('/api/v1/team/get-teams', payload);
  },
};

// Owner Services
export const ownerService = {
  // Basic CRUD operations
  getAllOwners: () => api.get('/api/v1/owner/getAllOwners'),
  createOwner: (ownerData) => api.post('/api/v1/owner/create-owner', ownerData),
  getOwnerDetails: (ownerId) => api.get(`/api/v1/owner/${ownerId}`),
  updateOwner: (ownerId, ownerData) => api.put(`/api/v1/owner/${ownerId}`, ownerData),
  deleteOwner: (ownerId) => api.delete(`/api/v1/owner/${ownerId}`),
  getOwnerGames: (ownerId) => api.get(`/api/v1/owner/${ownerId}/games`),
  
  // Team and player management
  addRetainedPlayer: (ownerId, playerData) => api.post(`/api/v1/owner/add-retained-player/${ownerId}`, playerData),
  sellPlayer: (ownerId, playerData) => api.post(`/api/v1/owner/admin/sold-players/${ownerId}`, playerData),
  getSoldPlayers: (ownerId) => api.get(`/api/v1/owner/sold-players/${ownerId}`),
  payEntryFee: (ownerId, entryAmountPaid) => api.post(`/api/v1/owner/pay-entry-fee/${ownerId}/${entryAmountPaid}`),
  getAllOwnerDetails: (gameId) => api.get(`/api/v1/owner/admin/getAllOwnerDetails/${gameId}`),
  
  // Playing XI management
  draftPlayersForPlayingXI: (draftData) => api.post('/api/v1/owner/draft-players-for-playing-11', draftData),
  removeDraftPlayersFromPlayingXI: (playerData) => api.post('/api/v1/owner/remove-draft-players-from-playing-11', playerData),
  draftCaptainViceCaptainForPlayingXI: (captainData) => api.post('/api/v1/owner/draft-candvc-for-playing-11', captainData),
  removeCaptainViceCaptainFromPlayingXI: (captainData) => api.post('/api/v1/owner/remove-candvc-from-playing-11', captainData),
  createPlayingXIUsingDraft: (draftData) => api.post('/api/v1/owner/create-playing-11-using-draft', draftData),
  createPlayingXI: (playingXIData) => api.post('/api/v1/owner/create-playing-11', playingXIData),
  
  // Player and captain swaps
  swapPlayer: (swapData) => api.post('/api/v1/owner/swap-player', swapData),
  swapCaptain: (swapData) => api.post('/api/v1/owner/swap-captain', swapData),
  swapViceCaptain: (swapData) => api.post('/api/v1/owner/swap-vice-captain', swapData),
  updateTeamOwnerCountry: (countryData) => api.post('/api/v1/owner/update-team-owner-country', countryData),
  
  // Admin notification services
  sendGameJoinNotification: (notificationData) => api.post('/api/v1/owner/admin/SendGameJoinNotification', notificationData),
};

// Venue Services
export const venueService = {
  createVenue: (seriesId) => api.get(`/api/v1/venue/create-venue/${seriesId}`),
};

// Game Services
export const gameService = {
  createGame: (seriesId) => api.get(`/api/v1/game/create-game/${seriesId}`),
  updateMaximumOwners: (gameId, ownersCount, maximumPoints, maximumMoneyGame, additionalData = {}) => 
    api.post(`/api/v1/game/updateMaximumOwners/${gameId}/${ownersCount}/${maximumPoints}/${maximumMoneyGame}`, additionalData),
  inviteOwners: (gameId) => api.get(`/api/v1/game/invite-owners/${gameId}`),
  joinGame: (gameId, ownerId) => api.get(`/api/v1/game/join-game/${gameId}/${ownerId}`),
  getAllGames: () => api.get('/api/v1/game/admin/getAllGames'),
  getAllMyGames: () => api.get('/api/v1/game/me/getAllMyGames'),
  getMyGames: () => api.get('/api/v1/game/me/getAllMyGames'), // Alias for consistency
  getSingleGame: (gameId) => api.get(`/api/v1/game/me/getSingleGame/${gameId}`),
  getGameDetails: (gameId) => api.get(`/api/v1/game/me/getSingleGame/${gameId}`), // Alias for getSingleGame
  createGameUsingExcel: (gameData) => api.post('/api/v1/game/create-game-using-excel', gameData),
  updateGameForRules: (gameId, rulesData) => api.post(`/api/v1/game/update-game-for-rules/${gameId}`, rulesData),
  updateAllowedPlayerType: (gameId, playerTypeData) => api.post(`/api/v1/game/update-allowed-player-type/${gameId}`, playerTypeData),
  updatePlayerCountryAndLeague: (gameId, updateData) => api.post(`/api/v1/game/admin/UpdateplayerCountryAndLeagueDetails/${gameId}`, updateData),
  // Note: These endpoints don't exist in backend - using sample data instead
  deleteGame: (gameId) => Promise.resolve({ data: { success: true, message: 'Game deleted successfully' } }),
  approveGame: (gameId) => Promise.resolve({ data: { success: true, message: 'Game approved successfully' } }),
  rejectGame: (gameId) => Promise.resolve({ data: { success: true, message: 'Game rejected successfully' } }),
  // Game Owner Details APIs - using mock data for now
  swapPlayers: (gameId, ownerId, swapData) => Promise.resolve({ data: { success: true, message: 'Players swapped successfully' } }),
  changeCaptain: (gameId, ownerId, captainData) => Promise.resolve({ data: { success: true, message: 'Captain changed successfully' } }),
  getMatchHistory: (gameId, ownerId) => Promise.resolve({ data: { success: true, data: [] } }),
  getSwapHistory: (gameId, ownerId) => Promise.resolve({ data: { success: true, data: [] } }),
};

// Series Services
export const seriesService = {
  createSeries: (seriesType) => api.get(`/api/v1/series/admin/create-series/${seriesType}`),
  updateSeriesDetails: (seriesId, seriesData) => api.post(`/api/v1/series/admin/updateSeriesDetails/${seriesId}`, seriesData),
  getSeriesDetails: (seriesId) => api.get(`/api/v1/series/getSeriesDetails/${seriesId}`),
  getAllSeries: () => api.get('/api/v1/series/getAllSeries'),
  getAvailableCountries: (seriesId = null) => {
    const params = seriesId ? { seriesId } : {};
    return api.get('/api/v1/series/getAvailableCountries', { params });
  },
  // Note: This endpoint doesn't exist in backend - using sample data instead
  getCurrentSeries: () => Promise.resolve({ data: { success: true, data: [] } }),
};

// Match Services
export const matchService = {
  createMatchFromSeries: (seriesId) => api.get(`/api/v1/match/create-match-from-series/${seriesId}`),
  getAllMatches: () => api.get('/api/v1/match/getAllMatches'),
  getMatchesBySeries: (seriesId) => api.get(`/api/v1/match/getMatchesBySeries/${seriesId}`),
};

// Point Services
export const pointService = {
  calculatePlayerPointsV2: (gameData) => api.post('/api/v1/point/calculate-player-points-v2', gameData),
  calculateMatchPoints: (gameId, matchId) => api.get(`/api/v1/point/calculate-match-points/${gameId}/${matchId}`),
  checkMatchCompleted: (gameId, matchId) => api.get(`/api/v1/point/CheckMatchCompleted/${gameId}/${matchId}`),
  fetchOwnersPointsForMatch: (gameId, matchId) => api.get(`/api/v1/point/FetchOwnersPointsForMatch/${gameId}/${matchId}`),
  getAllPointsForGame: (gameId) => api.get(`/api/v1/point/get-all-points/${gameId}`),
};

// Contest Services
export const contestService = {
  createContest: (matchId) => api.post(`/api/v1/contest/create-contest/${matchId}`),
  getContestById: (contestId) => api.get(`/api/v1/contest/contest/${contestId}`),
  getContestsByMatch: (matchId) => api.get(`/api/v1/contest/contests/match/${matchId}`),
  getAllContests: () => api.get('/api/v1/contest/contests'),
  getMyContests: () => api.get('/api/v1/contest/my-contests'),
};

// Contest Owner Services
export const contestOwnerService = {
  createContestOwner: (contestId, contestOwnerData) => api.post(`/api/v1/contestOwner/CrateContestOwner/${contestId}`, contestOwnerData),
  createTeam: (contestId, contestOwnerId, teamData) => api.post(`/api/v1/contestOwner/CreateTeam/${contestId}/${contestOwnerId}`, teamData),
  getContestOwner: (contestOwnerId) => api.get(`/api/v1/contestOwner/contest-owner/${contestOwnerId}`),
  getContestOwnersByContest: (contestId) => api.get(`/api/v1/contestOwner/contest/${contestId}/owners`),
  getMyContestOwners: () => api.get('/api/v1/contestOwner/my-contests'),
};

// CDS (Central Data Store) Services
export const cdsService = {
  createCDS: (cdsData) => api.post('/api/v1/cds/create-cds', cdsData),
};

// Logs Services
export const logsService = {
  createLogs: (logData) => api.post('/api/v1/logs/create-logs', logData),
  // Note: This endpoint doesn't exist in backend - using sample data instead
  getAllLogs: () => Promise.resolve({ data: { success: true, data: [] } }),
};

// Auction Services
export const auctionService = {
  // Create new auction
  createAuction: (auctionData) => api.post('/api/v1/auction/create', auctionData),
  
  // Start auction
  startAuction: (auctionId, settings) => api.post(`/api/v1/auction/${auctionId}/start`, settings),
  
  // Place bid
  placeBid: (auctionId, bidData) => api.post(`/api/v1/auction/${auctionId}/bid`, bidData),
  
  // Sell current player
  sellPlayer: (auctionId) => api.post(`/api/v1/auction/${auctionId}/sell`),
  
  // Mark player as unsold
  unsoldPlayer: (auctionId, reasonData) => api.post(`/api/v1/auction/${auctionId}/unsold`, reasonData),
  
  // Load random player from player type
  loadRandomPlayer: (auctionId, playerTypeData) => api.post(`/api/v1/auction/${auctionId}/load-random-player`, playerTypeData),
  
  // Pause auction
  pauseAuction: (auctionId) => api.post(`/api/v1/auction/${auctionId}/pause`),
  
  // Resume auction
  resumeAuction: (auctionId) => api.post(`/api/v1/auction/${auctionId}/resume`),
  
  // Get auction details
  getAuction: (auctionId) => api.get(`/api/v1/auction/${auctionId}`),
  
  // Join auction room
  joinAuctionRoom: (auctionId) => api.post(`/api/v1/auction/${auctionId}/join`),
  
  // Get auctions for a game
  getGameAuctions: (gameId) => api.get(`/api/v1/auction/game/${gameId}`),
};

// Notification Services
export const notificationService = {
  // Get user notifications
  getUserNotifications: () => api.get('/api/v1/notification/me/notifications'),
  
  // Get unread count
  getUnreadCount: () => api.get('/api/v1/notification/me/unread-count'),
  
  // Mark notification as read
  markAsRead: (notificationId) => api.put(`/api/v1/notification/${notificationId}/read`),
  
  // Mark all notifications as read
  markAllAsRead: () => api.put('/api/v1/notification/me/mark-all-read'),
  
  // Delete notification
  deleteNotification: (notificationId) => api.delete(`/api/v1/notification/${notificationId}`),
  
  // Create notification (admin)
  createNotification: (notificationData) => api.post('/api/v1/notification/create', notificationData),
  
  // Send game invitation notification
  sendGameInvitation: (invitationData) => api.post('/api/v1/notification/game-invitation', invitationData),
  
  // Admin notification management
  getAllNotifications: (params) => api.get('/api/v1/notification/admin/all', { params }),
  getNotificationStats: () => api.get('/api/v1/notification/admin/stats'),
  sendBroadcast: (broadcastData) => api.post('/api/v1/notification/admin/broadcast', broadcastData),
  updateNotification: (notificationId, updateData) => api.put(`/api/v1/notification/admin/${notificationId}`, updateData),
  deleteNotificationAdmin: (notificationId) => api.delete(`/api/v1/notification/admin/${notificationId}`),
  resendNotification: (notificationId) => api.post(`/api/v1/notification/admin/${notificationId}/resend`),
  getDeliveryReport: (notificationId) => api.get(`/api/v1/notification/admin/${notificationId}/delivery-report`),
  
  // Template management
  getAllTemplates: () => api.get('/api/v1/notification/admin/templates'),
  createTemplate: (templateData) => api.post('/api/v1/notification/admin/templates', templateData),
  updateTemplate: (templateId, templateData) => api.put(`/api/v1/notification/admin/templates/${templateId}`, templateData),
  deleteTemplate: (templateId) => api.delete(`/api/v1/notification/admin/templates/${templateId}`),
  
  // Analytics
  getAnalytics: (timeRange) => api.get(`/api/v1/notification/admin/analytics`, { params: { timeRange } }),
  getPerformanceReport: (filters) => api.get('/api/v1/notification/admin/performance', { params: filters }),
};